import { useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { initAnalytics, captureAttribution, trackEvent } from "@/lib/analytics";
import PopupInquiryModal from "@/components/PopupInquiryModal";
import { useSiteSeo, useCrawlGuard } from "@/lib/seo";
import { ADMIN_SLUG, ADMIN_BASE } from "@/lib/adminBase";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import MobileBottomNav from "@/components/MobileBottomNav";
import StudentMobileNav from "@/components/StudentMobileNav";
import ProtectedRoute from "@/components/ProtectedRoute";
import { ProdBlocked, StaffGated } from "@/components/DevRouteGuard";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import Home from "@/pages/Home";
import Catalog from "@/pages/Catalog";
import ProgramDetail from "@/pages/ProgramDetail";
import Domain from "@/pages/Domain";
import Institutes from "@/pages/Institutes";
import InstituteDetail from "@/pages/InstituteDetail";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import SuccessStoriesPage from "@/pages/SuccessStoriesPage";
import CorporateTraining from "@/pages/CorporateTraining";
import UpcomingBatches from "@/pages/UpcomingBatches";
import LegalPage from "@/pages/LegalPage";
import NotFound from "@/pages/NotFound";
import AuthorPage from "@/pages/AuthorPage";
import CategoryPage from "@/pages/CategoryPage";
import PathResolver from "@/pages/PathResolver";

const Login = lazy(() => import("@/pages/Login"));
const Signup = lazy(() => import("@/pages/Signup"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const Profile = lazy(() => import("@/pages/Profile"));
const VerifyEmail = lazy(() => import("@/pages/VerifyEmail"));
const Apply = lazy(() => import("@/pages/Apply"));
const Payment = lazy(() => import("@/pages/Payment"));
const Documents = lazy(() => import("@/pages/Documents"));
const ReferEarn = lazy(() => import("@/pages/ReferEarn"));
const ReferralDetail = lazy(() => import("@/pages/ReferralDetail"));
const RefRedirect = lazy(() => import("@/pages/RefRedirect"));
const MyTests = lazy(() => import("@/pages/MyTests"));
const ThankYou = lazy(() => import("@/pages/ThankYou"));
const TemplatePreviewFrame = lazy(() => import("@/pages/TemplatePreviewFrame"));
const PreviewHub = lazy(() => import("@/pages/preview/PreviewHub"));
const BentoPreview = lazy(() => import("@/pages/preview/BentoPreview"));
const CinematicPreview = lazy(() => import("@/pages/preview/CinematicPreview"));
const FinderPreview = lazy(() => import("@/pages/preview/FinderPreview"));
const EditorialPreview = lazy(() => import("@/pages/preview/EditorialPreview"));
const ExecutivePreview = lazy(() => import("@/pages/preview/ExecutivePreview"));
const FullPageAiPreview = lazy(() => import("@/pages/preview/FullPageAiPreview"));
const QaApp = lazy(() => import("@/pages/qa/QaApp"));

const AdminApp = lazy(() => import("@/pages/admin/AdminApp"));
const AdminLogin = lazy(() => import("@/pages/admin/AdminLogin"));

function PortalMobileChrome() {
  const location = useLocation();
  const inPortal = location.pathname === "/profile";
  if (inPortal) return <StudentMobileNav />;
  return <MobileBottomNav />;
}

function RouteLoader() {
  return <div className="min-h-screen bg-[#0A0F2B]" data-testid="admin-loading" />;
}

function FooterBottomSpacer() {
  const location = useLocation();
  const inPortal = location.pathname === "/profile";
  return (
    <>
      <div className="h-[calc(76px+env(safe-area-inset-bottom))] md:hidden bg-[#0F1115]" aria-hidden="true" />
      <PortalMobileChrome />
      {inPortal && <div className="md:hidden h-[calc(14px+env(safe-area-inset-top))]" aria-hidden="true" />}
    </>
  );
}

function PageTracker() {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith(ADMIN_BASE);
  useSiteSeo(isAdmin ? "__console__" : location.pathname);
  useCrawlGuard(isAdmin);
  useEffect(() => {
    window.scrollTo(0, 0);
    if (!isAdmin && location.pathname !== "/template-preview") trackEvent("page_view", { path: location.pathname });
  }, [location.pathname, isAdmin]);
  return null;
}

function FaviconLoader() {
  useEffect(() => {
    api.get("/website/sections/branding").then((r) => {
      const url = r.data?.favicon_url;
      if (!url) return;
      let link = document.querySelector("link[rel='icon']");
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
      }
      link.href = url;
      let apple = document.querySelector("link[rel='apple-touch-icon']");
      if (!apple) {
        apple = document.createElement("link");
        apple.rel = "apple-touch-icon";
        document.head.appendChild(apple);
      }
      apple.href = url;
    }).catch(() => {});
  }, []);
  return null;
}

function PublicNavbar() {
  const location = useLocation();
  const inPortal = location.pathname === "/profile";
  return (
    <div className={inPortal ? "max-md:hidden" : ""}>
      <Navbar />
    </div>
  );
}

function App() {
  useEffect(() => {
    captureAttribution();
    initAnalytics();
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
        <ImpersonationBanner />
          <Toaster position="top-right" richColors />
          <PageTracker />
          <FaviconLoader />
          <Routes>
            <Route path={`/${ADMIN_SLUG}/login`} element={<Suspense fallback={<RouteLoader />}><AdminLogin /></Suspense>} />
            <Route path={`/${ADMIN_SLUG}/*`} element={<ProtectedRoute admin><Suspense fallback={<RouteLoader />}><AdminApp /></Suspense></ProtectedRoute>} />
            <Route path="/template-preview" element={<StaffGated><TemplatePreviewFrame /></StaffGated>} />
            <Route path="/preview" element={<ProdBlocked><PreviewHub /></ProdBlocked>} />
            <Route path="/preview/bento" element={<ProdBlocked><BentoPreview /></ProdBlocked>} />
            <Route path="/preview/cinematic" element={<ProdBlocked><CinematicPreview /></ProdBlocked>} />
            <Route path="/preview/finder" element={<ProdBlocked><FinderPreview /></ProdBlocked>} />
            <Route path="/preview/editorial" element={<ProdBlocked><EditorialPreview /></ProdBlocked>} />
            <Route path="/preview/executive" element={<ProdBlocked><ExecutivePreview /></ProdBlocked>} />
            <Route path="/preview/fullpage-ai" element={<ProdBlocked><FullPageAiPreview /></ProdBlocked>} />
            <Route path="/qa/*" element={<ProdBlocked><QaApp /></ProdBlocked>} />
            <Route path="*" element={
              <div className="flex flex-col min-h-screen">
                <PublicNavbar />
                <main className="flex-1">
                  <Suspense fallback={null}>
                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/testing" element={<ProtectedRoute><MyTests /></ProtectedRoute>} />
                    <Route path="/courses" element={<Catalog />} />
                    <Route path="/programs" element={<Catalog />} />
                    <Route path="/courses/:slug" element={<ProgramDetail />} />
                    <Route path="/programs/:slug" element={<ProgramDetail />} />
                    <Route path="/domains" element={<Catalog />} />
                    <Route path="/domains/:slug" element={<Domain />} />
                    <Route path="/cloud-computing" element={<Domain fixedSlug="cloud-devops" canonicalPath="/cloud-computing" />} />
                    <Route path="/institutes" element={<Institutes />} />
                    <Route path="/institutes/:slug" element={<InstituteDetail />} />
                    <Route path="/blog" element={<Blog />} />
                    <Route path="/blog/:slug" element={<BlogPost />} />
                    <Route path="/blog/author/:slug" element={<AuthorPage />} />
                    <Route path="/blog/category/:slug" element={<CategoryPage />} />
                    <Route path="/thank-you" element={<ThankYou />} />
                    <Route path="/thank-you/:slug" element={<ThankYou />} />
                    <Route path="/privacy-policy" element={<LegalPage page="privacy" />} />
                    <Route path="/terms-conditions" element={<LegalPage page="terms" />} />
                    <Route path="/success-stories" element={<SuccessStoriesPage />} />
                    <Route path="/corporate-training" element={<CorporateTraining />} />
                    <Route path="/upcoming-batches" element={<UpcomingBatches />} />
                    <Route path="/privacy" element={<LegalPage page="privacy" />} />
                    <Route path="/terms" element={<LegalPage page="terms" />} />
                    <Route path="/disclaimer" element={<LegalPage page="disclaimer" />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/signup" element={<Signup />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/dashboard" element={<Navigate to="/profile" replace />} />
                    <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                    <Route path="/verify-email" element={<VerifyEmail />} />
                    <Route path="/apply/:appId" element={<ProtectedRoute><Apply /></ProtectedRoute>} />
                    <Route path="/pay/:appId" element={<ProtectedRoute><Payment /></ProtectedRoute>} />
                    <Route path="/documents/:appId" element={<ProtectedRoute><Documents /></ProtectedRoute>} />
                    <Route path="/refer-earn" element={<ProtectedRoute><ReferEarn /></ProtectedRoute>} />
                    <Route path="/refer-earn/:refId" element={<ProtectedRoute><ReferralDetail /></ProtectedRoute>} />
                    <Route path="/ref/:code" element={<RefRedirect />} />
                    <Route path="*" element={<PathResolver />} />
                  </Routes>
                  </Suspense>
                </main>
                <Footer />
                <FooterBottomSpacer />
                <PopupInquiryModal />
              </div>
            } />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
